import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
   Box,
   Drawer,
   AppBar,
   Toolbar,
   List,
   Typography,
   Divider,
   ListItem,
   ListItemButton,
   ListItemIcon,
   ListItemText,
   Avatar,
   IconButton,
   Menu,
   MenuItem,
   Tooltip,
   Badge,
   Collapse,
   Button,
   Select,
   FormControl,
   InputLabel,
   SelectChangeEvent,
   Paper,
   ListSubheader,
   useMediaQuery,
} from '@mui/material';
import {
   Dashboard as DashboardIcon,
   Map as MapIcon,
   Photo as PhotoIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Timeline as TimelineIcon,
   CalendarMonth as CalendarMonthIcon,
   Inventory as InventoryIcon,
   PhotoAlbum as PhotoAlbumIcon,
   DirectionsWalk,
   ListAlt as ItineraryIcon,
   LocalActivity as ActivitiesIcon,
   People as PeopleIcon,
   AccountCircle as AccountCircleIcon,
   Login as LoginIcon,
   ExitToApp as LogoutIcon,
   SupervisedUserCircle as AdminIcon,
   Notifications as NotificationsIcon,
   Settings as SettingsIcon,
   PersonAdd as PersonAddIcon,
   Group as GroupIcon,
   VolumeUp as VolumeUpIcon,
   Paid as PaidIcon,
   EventNote as EventNoteIcon,
   Email as EmailIcon,
   ConfirmationNumber as TicketIcon,
   Event as EventIcon,
   Explore as ExploreIcon,
   Mail as MailIcon,
   Payment as PaymentIcon,
   Chat as ChatIcon,
   Comment as CommentIcon,
   MenuBook as MenuBookIcon,
   Subscriptions as SubscriptionsIcon,
   ShoppingBag as ShoppingBagIcon,
   AccountBox as AccountBoxIcon,
   Add as AddIcon,
   Receipt as ReceiptIcon,
   Person as PersonIcon,
   MonetizationOn as MonetizationIcon,
   AdminPanelSettings as AdminPanelSettingsIcon,
   ArrowDropDown as ArrowDropDownIcon,
   Store as StoreIcon,
   LocalOffer as LocalOfferIcon,
   Storefront as StorefrontIcon,
   Restaurant as RestaurantIcon,
   Description as DescriptionIcon,
   BookOnline as BookOnlineIcon,
   RateReview as RateReviewIcon,
   Share as ShareIcon,
   VideoCall as VideoCallIcon,
   Home as HomeIcon,
   DynamicFeed as DynamicFeedIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useWebSocketChat } from '../contexts/WebSocketChatContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useVenue } from '../contexts/VenueContext';
import NotificationBell from './NotificationBell';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import LocationRequestModal from './common/LocationRequestModal';
import { useLocationModal } from '../hooks/useLocationModal';
import { getPendingInvitationCounts, InvitationCounts } from '../services/invitationCountsService';
import logo from '../assets/images/logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

// Admin user configuration
const ADMIN_USER_IDS = ['emmanueljones05@gmail.com'];
const ADMIN_EMAILS = ['emmanueljones05@gmail.com'];

const DRAWER_WIDTH = 270;

interface LayoutProps {
   children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
   const location = useLocation();
   const navigate = useNavigate();
   const isMobile = useMediaQuery('(max-width: 1000px)');
   const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
   const [pendingCallsCount, setPendingCallsCount] = useState(0);
   const [feedCount, setFeedCount] = useState(0);
   const [invitationCounts, setInvitationCounts] = useState<InvitationCounts>({
      trips: 0,
      events: 0,
      albums: 0,
      network: 0,
      total: 0,
   });
   const { currentUser, isAuthenticated, logout } = useUser();
   const { unreadCount } = useWebSocketChat();
   const { activeOrganization, organizations, isPersonalAccount, switchToOrganization } =
      useOrganization();
   const { hasSubscriptions } = useSubscription();
   const { venues, activeVenue, setActiveVenue } = useVenue();

   // Location modal
   const {
      showModal: showLocationModal,
      closeModal: closeLocationModal,
      skipModal: skipLocationModal,
      onLocationSaved,
      hasLocation,
   } = useLocationModal({
      userId: currentUser?.uid || '',
      isAuthenticated,
   });

   // State for menu
   const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [accountSwitcherAnchorEl, setAccountSwitcherAnchorEl] = useState<null | HTMLElement>(null);

   // State for expandable menu sections
   const [tripPlanningOpen, setTripPlanningOpen] = useState(true);
   const [userManagementOpen, setUserManagementOpen] = useState(false);

   // State for active account type
   const [activeAccountType, setActiveAccountType] = useState<
      'personal' | 'organization' | 'venue'
   >(activeVenue ? 'venue' : isPersonalAccount ? 'personal' : 'organization');

   // Check if user is an admin
   const isAdmin =
      currentUser &&
      (ADMIN_USER_IDS.includes(currentUser.uid) || ADMIN_EMAILS.includes(currentUser.email || ''));

   // Handle user menu open/close
   const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setUserMenuAnchorEl(event.currentTarget);
   };

   const handleUserMenuClose = () => {
      setUserMenuAnchorEl(null);
   };

   // Handle account switcher menu
   const handleAccountSwitcherOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAccountSwitcherAnchorEl(event.currentTarget);
   };

   const handleAccountSwitcherClose = () => {
      setAccountSwitcherAnchorEl(null);
   };

   // Handle switching accounts
   const handleSwitchAccount = (orgId: string | null) => {
      switchToOrganization(orgId);
      handleAccountSwitcherClose();

      // Clear venue when switching to personal or organization account
      setActiveVenue(null);

      // Update account type
      if (orgId === null) {
         setActiveAccountType('personal');
      } else {
         setActiveAccountType('organization');
      }

      // Determine the path type from the current location
      const currentPath = location.pathname;

      // Map of personal paths to their organization equivalents
      const pathMap: Record<string, string> = {
         '/dashboard': 'dashboard',
         '/map': 'map',
         '/upload': 'upload',
         '/timeline': 'timeline',
         '/calendar': 'calendar',
         '/events': 'events',
         '/trips': 'trips',
         '/people': 'people',
         '/chat': 'chat',
         '/invitations': 'invitations',
      };

      // Default to dashboard if no specific mapping
      let targetPath = 'dashboard';

      if (orgId === null) {
         // Switching to personal account
         // Extract the current feature from the organization path
         const orgPathMatch = currentPath.match(/\/organizations\/[^/]+\/([^/]+)/);
         if (orgPathMatch && orgPathMatch[1]) {
            // Find the personal equivalent path
            const personalPath = Object.entries(pathMap).find(
               ([_, value]) => value === orgPathMatch[1]
            )?.[0];
            if (personalPath) {
               navigate(personalPath);
               return;
            }
         }
         // Default to dashboard
         navigate('/dashboard');
      } else {
         // Switching to organization account
         // Find the equivalent organization path for the current personal path
         for (const [personalPath, featurePath] of Object.entries(pathMap)) {
            if (currentPath === personalPath || currentPath.startsWith(personalPath + '/')) {
               navigate(`/organizations/${orgId}/${featurePath}`);
               return;
            }
         }
         // Default to organization dashboard
         navigate(`/organizations/${orgId}/dashboard`);
      }
   };

   // Handle switching to venue account
   const handleSwitchToVenue = (venueId: string) => {
      handleAccountSwitcherClose();

      // Clear organization context when switching to venue
      switchToOrganization(null);

      // Set venue as active
      setActiveVenue(venueId);
      setActiveAccountType('venue');
      navigate(`/venues/${venueId}`);
   };

   // Handle switching to personal account
   const handleSwitchToPersonal = () => {
      handleSwitchAccount(null);
   };

   // Handle creating a new organization
   const handleCreateOrg = () => {
      handleAccountSwitcherClose();
      navigate('/organizations/create');
   };

   // Handle logout
   const handleLogout = async () => {
      handleUserMenuClose();
      await logout();
      navigate('/login');
   };

   // Toggle expandable menu sections
   const toggleTripPlanning = () => {
      setTripPlanningOpen(!tripPlanningOpen);
   };

   const toggleUserManagement = () => {
      setUserManagementOpen(!userManagementOpen);
   };

   // Update active account type when dependencies change
   useEffect(() => {
      if (activeVenue) {
         setActiveAccountType('venue');
      } else if (isPersonalAccount) {
         setActiveAccountType('personal');
      } else {
         setActiveAccountType('organization');
      }
   }, [activeVenue, isPersonalAccount]);

   // Count pending scheduled calls for badge
   useEffect(() => {
      if (!currentUser || !isPersonalAccount) {
         setPendingCallsCount(0);
         return;
      }

      // Query for received calls that are pending
      const receivedQuery = query(
         collection(db, 'scheduledCalls'),
         where('invitedUser', '==', currentUser.uid)
      );

      const unsubscribe = onSnapshot(
         receivedQuery,
         (snapshot) => {
            console.log('Pending calls query returned:', snapshot.size, 'documents');
            let pendingCount = 0;

            snapshot.forEach((doc) => {
               const data = doc.data();
               // Only count calls that are not cancelled or completed
               if (data.status === 'cancelled' || data.status === 'completed') {
                  return;
               }

               // Find the current user as participant (invitee)
               const participants = data.participants || [];
               const userParticipant = participants.find(
                  (p: any) => p.userId === currentUser.uid && p.role === 'participant'
               );

               // Count calls where user is invitee with pending status
               if (userParticipant?.status === 'pending') {
                  pendingCount++;
               }
            });

            setPendingCallsCount(pendingCount);
         },
         (error) => {
            setPendingCallsCount(0);
         }
      );

      return () => unsubscribe();
   }, [currentUser, isPersonalAccount]);

   // Fetch feed count (album access requests)
   useEffect(() => {
      if (!currentUser || !isPersonalAccount) {
         setFeedCount(0);
         return;
      }

      // Query for unseen feed cards where user is owner or sender
      const feedQuery = query(
         collection(db, 'feed'),
         where('card_type', '==', 'album_access_request'),
         where('seen', '==', false)
      );

      const unsubscribe = onSnapshot(
         feedQuery,
         (snapshot) => {
            let count = 0;
            snapshot.forEach((doc) => {
               const data = doc.data();
               // Count cards where current user is owner or sender
               if (data.userid === currentUser.uid || data.senderid === currentUser.uid) {
                  count++;
               }
            });
            console.log('Feed count updated:', count);
            setFeedCount(count);
         },
         (error) => {
            console.error('Error fetching feed count:', error);
            setFeedCount(0);
         }
      );

      return () => unsubscribe();
   }, [currentUser, isPersonalAccount]);

   // Fetch invitation counts
   useEffect(() => {
      if (!currentUser || !isPersonalAccount || !currentUser.email) {
         setInvitationCounts({
            trips: 0,
            events: 0,
            albums: 0,
            network: 0,
            total: 0,
         });
         return;
      }

      const fetchCounts = async () => {
         try {
            const counts = await getPendingInvitationCounts(currentUser.email!, currentUser.uid);
            setInvitationCounts(counts);
         } catch (error) {
            console.error('Error fetching invitation counts:', error);
            setInvitationCounts({
               trips: 0,
               events: 0,
               albums: 0,
               network: 0,
               total: 0,
            });
         }
      };

      fetchCounts();

      // Refresh counts every 2 minutes
      const interval = setInterval(fetchCounts, 2 * 60 * 1000);
      return () => clearInterval(interval);
   }, [currentUser, isPersonalAccount]);

   // Get the current context label
   const getCurrentContextLabel = () => {
      if (activeVenue) {
         return `${activeVenue.name} (Venue)`;
      } else if (isPersonalAccount) {
         return 'Personal Account';
      } else if (activeOrganization) {
         return activeOrganization.name;
      }
      return 'Account';
   };

   const getAvatarSrc = () => {
      if (isPersonalAccount) {
         return currentUser?.photoURL || undefined;
      } else if (activeOrganization) {
         return activeOrganization.logoUrl || undefined;
      } else if (activeVenue) {
         return activeVenue.logoUrl || undefined;
      }
      return undefined;
   };

   console.log('isMobile', isMobile);
   return (
      <Box sx={{ display: 'flex' }}>
         {/* App Bar */}
         <AppBar
            sx={{
               borderRadius: 0,
            }}
         >
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
               {isMobile && (
                  <IconButton onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}>
                     <MenuIcon />
                  </IconButton>
               )}
               {!isMobile && (
                  <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                     {activeVenue
                        ? 'Venue Account'
                        : isPersonalAccount
                          ? 'Personal Account'
                          : 'Organization Account'}
                  </Typography>
               )}
               {isAuthenticated ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     {/* Notifications */}
                     <NotificationBell color='inherit' />

                     {/* Account Switcher Button */}
                     {!isMobile && (
                        <Box display='flex' alignItems='center' mr={1}>
                           <Tooltip title='Switch Account'>
                              <Button
                                 onClick={handleAccountSwitcherOpen}
                                 color='inherit'
                                 aria-haspopup='true'
                                 startIcon={
                                    activeVenue ? (
                                       <StoreIcon />
                                    ) : isPersonalAccount ? (
                                       <PersonIcon />
                                    ) : (
                                       <GroupIcon />
                                    )
                                 }
                                 endIcon={<ArrowDropDownIcon />}
                                 sx={{
                                    textTransform: 'none',
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for buttons
                                    fontSize: '0.875rem',
                                    borderRadius: '10px',
                                    px: 1.5,
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                    border: '1px solid rgba(255, 255, 255, 0.07)',
                                    '&:hover': {
                                       backgroundColor: 'rgba(255, 255, 255, 0.09)',
                                       borderColor: 'rgba(255, 255, 255, 0.14)',
                                       color: '#FFFFFF',
                                    },
                                 }}
                              >
                                 {getCurrentContextLabel()}
                              </Button>
                           </Tooltip>
                        </Box>
                     )}

                     {/* User Menu */}
                     <Tooltip title='Account'>
                        <IconButton
                           onClick={handleUserMenuOpen}
                           sx={{ ml: 1 }}
                           aria-controls='user-menu'
                           aria-haspopup='true'
                        >
                           {getAvatarSrc() ? (
                              <Avatar
                                 src={getAvatarSrc()}
                                 alt={currentUser?.displayName || undefined}
                              />
                           ) : (
                              <Avatar>{currentUser?.displayName?.charAt(0) || 'U'}</Avatar>
                           )}
                        </IconButton>
                     </Tooltip>
                  </Box>
               ) : (
                  <Button
                     color='inherit'
                     startIcon={<LoginIcon />}
                     component={RouterLink}
                     to='/login'
                  >
                     Login
                  </Button>
               )}
            </Toolbar>
         </AppBar>

         {/* Account Menu */}
         <Menu
            id='user-menu'
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'right',
            }}
            transformOrigin={{
               vertical: 'top',
               horizontal: 'right',
            }}
         >
            <MenuItem
               onClick={() => {
                  handleUserMenuClose();
                  navigate('/profile');
               }}
            >
               <ListItemIcon>
                  <AccountCircleIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary='Profile' />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
               <ListItemIcon>
                  <LogoutIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText primary='Logout' />
            </MenuItem>
         </Menu>

         {/* Sidebar */}
         <Drawer
            open={mobileDrawerOpen || !isMobile}
            onClose={() => setMobileDrawerOpen(false)}
            variant={isMobile ? 'temporary' : 'permanent'}
            sx={{
               width: DRAWER_WIDTH,
               flexShrink: 0,
               [`& .MuiDrawer-paper`]: {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  backgroundColor: '#0E1118', // bgSurface from theme
                  backgroundImage: 'none', // Remove MUI elevation gradient
                  borderRight: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                  boxShadow: 'none',
               },
            }}
         >
            <Toolbar sx={{ py: 2 }}>
               {/* logo */}
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1.5,
                     justifyContent: 'space-between',
                     width: '100%',
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                     <img
                        src={logo}
                        alt='logo'
                        style={{
                           width: 40,
                           height: 40,
                           borderRadius: 8,
                           boxShadow: '0 2px 8px rgba(1, 148, 154, 0.2)',
                        }}
                     />
                     <Typography
                        variant='h6'
                        component='div'
                        sx={{
                           fontFamily: '"Cormorant Garamond", serif', // Display font for logo
                           fontWeight: 600,
                           color: '#00C4A7', // Teal/green brand accent
                           fontSize: '1.1rem',
                        }}
                     >
                        WeTrippen
                     </Typography>
                  </Box>
                  {isMobile && mobileDrawerOpen && (
                     <IconButton
                        onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                        sx={{
                           color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                           '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                           },
                        }}
                     >
                        <CloseIcon />
                     </IconButton>
                  )}
               </Box>
            </Toolbar>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.07)' }} />

            {isAuthenticated && (
               <Box sx={{ p: 2 }}>
                  <Button
                     fullWidth
                     variant='outlined'
                     onClick={handleAccountSwitcherOpen}
                     endIcon={<ArrowDropDownIcon />}
                     sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.07)',
                        color: 'rgba(255, 255, 255, 0.60)',
                        '&:hover': {
                           backgroundColor: 'rgba(255, 255, 255, 0.09)',
                           borderColor: 'rgba(255, 255, 255, 0.14)',
                           color: '#FFFFFF',
                        },
                     }}
                  >
                     <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar
                           src={getAvatarSrc()}
                           sx={{
                              width: 32,
                              height: 32,
                              mr: 1.5,
                              bgcolor: activeVenue
                                 ? 'primary.main'
                                 : isPersonalAccount
                                   ? 'primary.main'
                                   : 'secondary.main',
                           }}
                        >
                           {activeVenue
                              ? activeVenue.name.charAt(0)
                              : isPersonalAccount
                                ? currentUser?.displayName?.charAt(0) || 'U'
                                : activeOrganization?.name.charAt(0) || 'O'}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           <Typography 
                              variant='body2' 
                              noWrap
                              sx={{ color: '#FFFFFF' }} // textPrimary from theme
                           >
                              {getCurrentContextLabel()}
                           </Typography>
                           <Typography
                              variant='caption'
                              display='block'
                              noWrap
                              sx={{ 
                                 fontFamily: '"DM Mono", monospace', // DM Mono for labels
                                 color: 'rgba(255, 255, 255, 0.35)', // textMuted from theme
                                 fontSize: '0.6875rem',
                                 letterSpacing: '0.08em',
                              }}
                           >
                              {activeVenue
                                 ? 'VENUE'
                                 : isPersonalAccount
                                   ? 'PERSONAL ACCOUNT'
                                   : 'ORGANIZATION'}
                           </Typography>
                        </Box>
                     </Box>
                  </Button>
               </Box>
            )}

            {/* Account Switcher Menu */}
            <Menu
               id='account-switcher-menu'
               anchorEl={accountSwitcherAnchorEl}
               open={Boolean(accountSwitcherAnchorEl)}
               onClose={handleAccountSwitcherClose}
               PaperProps={{
                  sx: { width: 280, maxHeight: 400 },
               }}
            >
               <MenuItem
                  onClick={() => handleSwitchToPersonal()}
                  selected={isPersonalAccount && !activeVenue}
                  sx={{ py: 1.5 }}
               >
                  <ListItemIcon>
                     <Avatar
                        src={currentUser?.photoURL || undefined}
                        sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                     >
                        {currentUser?.displayName?.charAt(0) || 'U'}
                     </Avatar>
                  </ListItemIcon>
                  <ListItemText
                     primary={currentUser?.displayName || 'Personal Account'}
                     secondary='Personal Account'
                  />
               </MenuItem>

               {venues && venues.length > 0 && (
                  <>
                     <Divider />
                     <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ px: 2, py: 1, display: 'block' }}
                     >
                        Your Venues
                     </Typography>

                     {venues &&
                        venues.map((venue) => (
                           <MenuItem
                              key={venue.id}
                              onClick={() => handleSwitchToVenue(venue.id)}
                              selected={activeVenue && activeVenue.id === venue.id ? true : false}
                              sx={{ py: 1.5 }}
                           >
                              <ListItemIcon>
                                 <Avatar
                                    src={venue.logoUrl || undefined}
                                    sx={{
                                       width: 32,
                                       height: 32,
                                       bgcolor: 'secondary.main',
                                    }}
                                 >
                                    {venue.name.charAt(0)}
                                 </Avatar>
                              </ListItemIcon>
                              <ListItemText primary={venue.name} secondary='Venue' />
                           </MenuItem>
                        ))}
                  </>
               )}

               {organizations && organizations.length > 0 && (
                  <>
                     <Divider />
                     <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ px: 2, py: 1, display: 'block' }}
                     >
                        Your Organizations
                     </Typography>

                     {organizations &&
                        organizations.map((org) => (
                           <MenuItem
                              key={org.id}
                              onClick={() => handleSwitchAccount(org.id)}
                              selected={
                                 !isPersonalAccount &&
                                 !activeVenue &&
                                 activeOrganization?.id === org.id
                              }
                              sx={{ py: 1.5 }}
                           >
                              <ListItemIcon>
                                 <Avatar
                                    src={org.logoUrl || undefined}
                                    sx={{
                                       width: 32,
                                       height: 32,
                                       bgcolor: 'secondary.main',
                                    }}
                                 >
                                    {org.name.charAt(0)}
                                 </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                 primary={org.name}
                                 secondary={
                                    org.creatorId === currentUser?.uid
                                       ? 'Owner'
                                       : org.members.find((m) => m.userId === currentUser?.uid)
                                            ?.role || 'Member'
                                 }
                              />
                           </MenuItem>
                        ))}
                  </>
               )}

               <Divider />
               <MenuItem onClick={handleCreateOrg} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                     <Avatar sx={{ bgcolor: 'action.selected' }}>
                        <AddIcon />
                     </Avatar>
                  </ListItemIcon>
                  <ListItemText primary='Create New Organization' />
               </MenuItem>
            </Menu>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.07)', mx: 2 }} />

            {isAuthenticated ? (
               <>
                  {/* Feed - Only for personal accounts */}
                  {isPersonalAccount && (
                     <List sx={{ px: 1, pt: 2 }}>
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/feed'
                              selected={location.pathname === '/feed'}
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                                 color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                 backgroundColor:
                                    location.pathname === '/feed'
                                       ? 'rgba(0, 196, 167, 0.10)' // tealDim from theme
                                       : 'transparent',
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                    color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                       backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    color: 'rgba(255, 255, 255, 0.75)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <DynamicFeedIcon />
                              </ListItemIcon>
                              <ListItemText
                                 primary={
                                    <Box
                                       sx={{
                                          position: 'relative',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                       }}
                                    >
                                       <Typography
                                          sx={{
                                             fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                             fontSize: '0.8125rem',
                                             fontWeight: 500,
                                             color: 'inherit',
                                          }}
                                       >
                                          Feed
                                       </Typography>
                                       {feedCount > 0 && (
                                          <Box
                                             sx={{
                                                position: 'absolute',
                                                top: -12,
                                                left: '100%',
                                                ml: 0.5,
                                                backgroundColor: '#ef4444',
                                                color: '#ffffff',
                                                borderRadius: '10px',
                                                minWidth: '18px',
                                                height: '18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                padding: '0 4px',
                                             }}
                                          >
                                             {feedCount}
                                          </Box>
                                       )}
                                    </Box>
                                 }
                              />
                           </ListItemButton>
                        </ListItem>
                     </List>
                  )}

                  {/* Main Navigation with Categories */}
                  <List
                     subheader={
                        <ListSubheader
                           component='div'
                           sx={{
                              backgroundColor: 'transparent',
                              fontFamily: '"DM Mono", monospace', // DM Mono for section labels
                              color: 'rgba(255, 255, 255, 0.35)', // textMuted from theme
                              fontWeight: 500,
                              fontSize: '0.625rem',
                              letterSpacing: '0.15em',
                              textTransform: 'uppercase',
                              py: 1,
                              px: 2,
                              mt: 1,
                           }}
                        >
                           OVERVIEW
                        </ListSubheader>
                     }
                     sx={{ px: 1 }}
                  >
                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/dashboard'
                                 : `/organizations/${activeOrganization?.id}/dashboard`
                           }
                           selected={
                              location.pathname === '/dashboard' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/dashboard`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                 },
                              },
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <DashboardIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Dashboard'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     {/* Only show Profile for personal accounts */}
                     {isPersonalAccount && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/profile'
                              selected={location.pathname === '/profile'}
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                    color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                       backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <PersonIcon />
                              </ListItemIcon>
                              <ListItemText
                                 primary='Profile'
                                 primaryTypographyProps={{
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'inherit',
                                 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     )}
                  </List>

                  {/* Experiences Section */}
                  <List
                     subheader={
                        <ListSubheader
                           component='div'
                           sx={{
                              backgroundColor: 'transparent',
                              fontFamily: '"DM Mono", monospace', // DM Mono for section labels
                              color: 'rgba(255, 255, 255, 0.35)', // textMuted from theme
                              fontWeight: 500,
                              fontSize: '0.625rem',
                              letterSpacing: '0.15em',
                              textTransform: 'uppercase',
                              py: 1,
                              px: 2,
                              mt: 2,
                           }}
                        >
                           EXPERIENCES
                        </ListSubheader>
                     }
                     sx={{ px: 1 }}
                  >
                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/events'
                                 : `/organizations/${activeOrganization?.id}/events`
                           }
                           selected={
                              location.pathname === '/events' ||
                              location.pathname.startsWith('/events/') ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/events` ||
                              location.pathname.startsWith(
                                 `/organizations/${activeOrganization?.id}/events/`
                              )
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              {isPersonalAccount ? (
                                 <Badge
                                    badgeContent={invitationCounts.events}
                                    color='error'
                                    invisible={invitationCounts.events === 0}
                                    sx={{
                                       '& .MuiBadge-badge': {
                                          fontSize: '0.65rem',
                                          minWidth: '16px',
                                          height: '16px',
                                       },
                                    }}
                                 >
                                    <EventIcon />
                                 </Badge>
                              ) : (
                                 <Box sx={{ display: 'flex' }}>
                                    <EventIcon />
                                    <MonetizationIcon
                                       fontSize='small'
                                       sx={{ marginLeft: -8, marginTop: -8, color: 'inherit' }}
                                    />
                                 </Box>
                              )}
                           </ListItemIcon>
                           <ListItemText
                              primary={isPersonalAccount ? 'Events' : 'Events (Paid)'}
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     {/* Trips - Hidden per user request */}
                     {/* <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/trips'
                                 : `/organizations/${activeOrganization?.id}/trips`
                           }
                           selected={
                              location.pathname === '/trips' ||
                              location.pathname.startsWith('/trip/')
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <Badge
                                 badgeContent={invitationCounts.trips}
                                 color='error'
                                 invisible={invitationCounts.trips === 0}
                                 sx={{
                                    '& .MuiBadge-badge': {
                                       fontSize: '0.65rem',
                                       minWidth: '16px',
                                       height: '16px',
                                    },
                                 }}
                              >
                                 <InventoryIcon />
                              </Badge>
                           </ListItemIcon>
                           <ListItemText
                              primary='Trips'
                              primaryTypographyProps={{
                                 fontSize: '0.875rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem> */}

                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/upload'
                                 : `/organizations/${activeOrganization?.id}/upload`
                           }
                           selected={
                              location.pathname === '/upload' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/upload`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <PhotoIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Manage Photos'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     {/* Map View - Hidden per user request */}
                     {/* <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/map'
                                 : `/organizations/${activeOrganization?.id}/map`
                           }
                           selected={
                              location.pathname === '/map' ||
                              location.pathname === `/organizations/${activeOrganization?.id}/map`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <MapIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Map View'
                              primaryTypographyProps={{
                                 fontSize: '0.875rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem> */}

                     {/* Timeline - Hidden per user request */}
                     {/* <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/timeline'
                                 : `/organizations/${activeOrganization?.id}/timeline`
                           }
                           selected={
                              location.pathname === '/timeline' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/timeline`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <TimelineIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Timeline'
                              primaryTypographyProps={{
                                 fontSize: '0.875rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem> */}

                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/calendar'
                                 : `/organizations/${activeOrganization?.id}/calendar`
                           }
                           selected={
                              location.pathname === '/calendar' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/calendar`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              // Remove hardcoded selected styles - let theme handle it
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <CalendarMonthIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Calendar'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/albums'
                                 : `/organizations/${activeOrganization?.id}/albums`
                           }
                           selected={
                              isPersonalAccount &&
                              (location.pathname === '/albums' ||
                                 location.pathname.startsWith('/albums/'))
                              // || (!isPersonalAccount && (location.pathname === `/organizations/${activeOrganization?.id}/albums` || location.pathname.startsWith(`/organizations/${activeOrganization?.id}/albums/`)))
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                 },
                              },
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <Badge
                                 badgeContent={invitationCounts.albums}
                                 color='error'
                                 invisible={invitationCounts.albums === 0}
                                 sx={{
                                    '& .MuiBadge-badge': {
                                       fontSize: '0.65rem',
                                       minWidth: '16px',
                                       height: '16px',
                                    },
                                 }}
                              >
                                 <PhotoAlbumIcon />
                              </Badge>
                           </ListItemIcon>
                           <ListItemText
                              primary='Photo Albums'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to='/shared-collections'
                           selected={location.pathname === '/shared-collections'}
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                 },
                              },
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <ShareIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='My Photos'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>
                  </List>

                  {/* Explore Section - Only for Organization accounts */}
                  {!isPersonalAccount && (
                     <List subheader={<ListSubheader component='div'>🔍 Explore</ListSubheader>}>
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/explore-events'
                              selected={location.pathname === '/explore-events'}
                           >
                              <ListItemIcon sx={{ color: '#FBBC05' }}>
                                 <ExploreIcon />
                              </ListItemIcon>
                              <ListItemText primary='Explore Events' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/explore-trips'
                              selected={location.pathname === '/explore-trips'}
                           >
                              <ListItemIcon sx={{ color: '#FBBC05' }}>
                                 <ExploreIcon />
                              </ListItemIcon>
                              <ListItemText primary='Explore Trips' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/explore-subscriptions'
                              selected={location.pathname === '/explore-subscriptions'}
                           >
                              <ListItemIcon sx={{ color: '#FBBC05' }}>
                                 <MenuBookIcon />
                              </ListItemIcon>
                              <ListItemText primary='Explore Subscriptions' />
                           </ListItemButton>
                        </ListItem>
                     </List>
                  )}

                  {/* Social Section */}
                  <List
                     subheader={
                        <ListSubheader
                           component='div'
                           sx={{
                              backgroundColor: 'transparent',
                              fontFamily: '"DM Mono", monospace', // DM Mono for section labels
                              color: 'rgba(255, 255, 255, 0.35)', // textMuted from theme
                              fontWeight: 500,
                              fontSize: '0.625rem',
                              letterSpacing: '0.15em',
                              textTransform: 'uppercase',
                              py: 1,
                              px: 2,
                              mt: 2,
                           }}
                        >
                           SOCIAL
                        </ListSubheader>
                     }
                     sx={{ px: 1 }}
                  >
                     {/* Add People menu item for personal accounts */}
                     {isPersonalAccount && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/people'
                              selected={location.pathname === '/people'}
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                 '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <Badge
                                    badgeContent={invitationCounts.network}
                                    color='error'
                                    invisible={invitationCounts.network === 0}
                                    sx={{
                                       '& .MuiBadge-badge': {
                                          fontSize: '0.65rem',
                                          minWidth: '16px',
                                          height: '16px',
                                       },
                                    }}
                                 >
                                    <PeopleIcon />
                                 </Badge>
                              </ListItemIcon>
                              <ListItemText
                                 primary='People'
                                 primaryTypographyProps={{
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'inherit',
                                 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     )}

                     {/* Add Scheduled Calls menu item for personal accounts */}
                     {isPersonalAccount && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/scheduled-calls'
                              selected={location.pathname === '/scheduled-calls'}
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                 '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <Badge
                                    badgeContent={pendingCallsCount}
                                    color='error'
                                    invisible={pendingCallsCount === 0}
                                 >
                                    <VideoCallIcon />
                                 </Badge>
                              </ListItemIcon>
                              <ListItemText
                                 primary='Scheduled Calls'
                                 primaryTypographyProps={{
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'inherit',
                                 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     )}

                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/invitations'
                                 : `/organizations/${activeOrganization?.id}/invitations`
                           }
                           selected={
                              location.pathname === '/invitations' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/invitations`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                 '& .MuiListItemIcon-root': {
                                    color: '#00C4A7', // teal from theme
                                 },
                                 '& .MuiListItemText-primary': {
                                    color: '#00C4A7', // teal from theme
                                    fontWeight: 600,
                                 },
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                 },
                              },
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <Badge badgeContent={0} color='error'>
                                 <MailIcon />
                              </Badge>
                           </ListItemIcon>
                           <ListItemText
                              primary='Manage Invitations'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     {/* Communications for personal accounts */}

                     {!isPersonalAccount && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to={`/organizations/${activeOrganization?.id}/contacts`}
                              selected={
                                 location.pathname ===
                                 `/organizations/${activeOrganization?.id}/contacts`
                              }
                           >
                              <ListItemIcon sx={{ color: '#34A853' }}>
                                 <PeopleIcon />
                              </ListItemIcon>
                              <ListItemText primary='Contacts' />
                           </ListItemButton>
                        </ListItem>
                     )}

                     {/* AI Photo Studio */}
                     {isPersonalAccount && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/ai-photo-studio'
                              selected={
                                 location.pathname === '/ai-photo-studio' ||
                                 location.pathname.startsWith('/ai-photo-studio/')
                              }
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                 '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <PhotoLibraryIcon />
                              </ListItemIcon>
                              <ListItemText
                                 primary='AI Photo Studio'
                                 primaryTypographyProps={{
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'inherit',
                                 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     )}
                  </List>

                  {/* Purchases Section - Only for Organization accounts */}
                  {!isPersonalAccount && (
                     <List subheader={<ListSubheader component='div'>🛍️ Purchases</ListSubheader>}>
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/my-tickets'
                              selected={location.pathname === '/my-tickets'}
                           >
                              <ListItemIcon sx={{ color: '#9C27B0' }}>
                                 <TicketIcon />
                              </ListItemIcon>
                              <ListItemText primary='My Tickets' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/my-subscriptions'
                              selected={location.pathname === '/my-subscriptions'}
                           >
                              <ListItemIcon sx={{ color: '#9C27B0' }}>
                                 <SubscriptionsIcon />
                              </ListItemIcon>
                              <ListItemText primary='My Subscriptions' />
                           </ListItemButton>
                        </ListItem>
                     </List>
                  )}

                  {/* Account Management */}
                  <List
                     subheader={
                        <ListSubheader
                           component='div'
                           sx={{
                              backgroundColor: 'transparent',
                              fontFamily: '"DM Mono", monospace', // DM Mono for section labels
                              color: 'rgba(255, 255, 255, 0.35)', // textMuted from theme
                              fontWeight: 500,
                              fontSize: '0.625rem',
                              letterSpacing: '0.15em',
                              textTransform: 'uppercase',
                              py: 1,
                              px: 2,
                              mt: 2,
                           }}
                        >
                           ACCOUNT
                        </ListSubheader>
                     }
                     sx={{ px: 1 }}
                  >
                     <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={
                              isPersonalAccount
                                 ? '/subscription'
                                 : `/organizations/${activeOrganization?.id}/subscriptions`
                           }
                           selected={
                              location.pathname === '/subscription' ||
                              location.pathname ===
                                 `/organizations/${activeOrganization?.id}/subscriptions`
                           }
                           sx={{
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                              '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                 '& .MuiListItemIcon-root': {
                                    color: '#00C4A7', // teal from theme
                                 },
                                 '& .MuiListItemText-primary': {
                                    color: '#00C4A7', // teal from theme
                                    fontWeight: 600,
                                 },
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                 },
                              },
                              '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                              },
                           }}
                        >
                           <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                              <PaymentIcon />
                           </ListItemIcon>
                           <ListItemText
                              primary='Subscription'
                              primaryTypographyProps={{
                                 fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                 fontSize: '0.8125rem',
                                 fontWeight: 500,
                                 color: 'inherit',
                              }}
                           />
                        </ListItemButton>
                     </ListItem>

                     {/* <ListItem disablePadding>
                        <ListItemButton
                           component={RouterLink}
                           to={`/organizations/${activeOrganization?.id}`}
                           selected={
                              location.pathname === `/organizations/${activeOrganization?.id}`
                           }
                        >
                           <ListItemIcon sx={{ color: '#607D8B' }}>
                              <AccountBoxIcon />
                           </ListItemIcon>
                           <ListItemText primary='My Account' />
                        </ListItemButton>
                     </ListItem> */}

                     {isAdmin && (
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/admin'
                              selected={location.pathname === '/admin'}
                              sx={{
                                 borderRadius: 2,
                                 mx: 1,
                                 mb: 0.5,
                              color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                                 '&.Mui-selected': {
                                 backgroundColor: 'rgba(0, 196, 167, 0.10)', // tealDim from theme
                                 color: '#00C4A7', // teal from theme
                                    '&:hover': {
                                    backgroundColor: 'rgba(0, 196, 167, 0.15)',
                                    },
                                 },
                                 '&:hover': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.75)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                 <AdminPanelSettingsIcon />
                              </ListItemIcon>
                              <ListItemText
                                 primary='Administration'
                                 primaryTypographyProps={{
                                    fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'inherit',
                                 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     )}
                  </List>

                  {/* Venue Management - Only for Organization accounts */}
                  {!isPersonalAccount && (
                     <List
                        subheader={
                           <ListSubheader component='div'>🏨 Venue Management</ListSubheader>
                        }
                     >
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/venues'
                              selected={location.pathname === '/venues'}
                           >
                              <ListItemIcon sx={{ color: '#4CAF50' }}>
                                 <StoreIcon />
                              </ListItemIcon>
                              <ListItemText primary='My Venues' />
                           </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to='/venues/create'
                              selected={location.pathname === '/venues/create'}
                           >
                              <ListItemIcon sx={{ color: '#4CAF50' }}>
                                 <AddIcon />
                              </ListItemIcon>
                              <ListItemText primary='Add New Venue' />
                           </ListItemButton>
                        </ListItem>
                     </List>
                  )}

                  {/* Active Venue Management - Only visible when in venue mode */}
                  {activeVenue && (
                     <>
                        <ListSubheader
                           sx={{
                              backgroundColor: 'transparent',
                              color: 'primary.main',
                              fontWeight: 'bold',
                              mt: 2,
                           }}
                        >
                           Venue Management
                        </ListSubheader>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to={`/venues/${activeVenue.id}/dashboard`}
                              selected={location.pathname === `/venues/${activeVenue.id}/dashboard`}
                              sx={{
                                 borderRadius: '0 20px 20px 0',
                                 pl: 2,
                                 pr: 1,
                                 py: 1,
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)', // Green for venue items
                                 },
                                 '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                 <DashboardIcon
                                    color={
                                       location.pathname === `/venues/${activeVenue.id}/dashboard`
                                          ? 'primary'
                                          : 'inherit'
                                    }
                                 />
                              </ListItemIcon>
                              <ListItemText primary='Venue Dashboard' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to={`/venues/${activeVenue.id}/packages`}
                              selected={location.pathname.startsWith(
                                 `/venues/${activeVenue.id}/packages`
                              )}
                              sx={{
                                 borderRadius: '0 20px 20px 0',
                                 pl: 2,
                                 pr: 1,
                                 py: 1,
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                 },
                                 '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                 <LocalOfferIcon
                                    color={
                                       location.pathname.startsWith(
                                          `/venues/${activeVenue.id}/packages`
                                       )
                                          ? 'primary'
                                          : 'inherit'
                                    }
                                 />
                              </ListItemIcon>
                              <ListItemText primary='Packages' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to={`/venues/${activeVenue.id}/bookings`}
                              selected={location.pathname.startsWith(
                                 `/venues/${activeVenue.id}/bookings`
                              )}
                              sx={{
                                 borderRadius: '0 20px 20px 0',
                                 pl: 2,
                                 pr: 1,
                                 py: 1,
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                 },
                                 '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                 <BookOnlineIcon
                                    color={
                                       location.pathname.startsWith(
                                          `/venues/${activeVenue.id}/bookings`
                                       )
                                          ? 'primary'
                                          : 'inherit'
                                    }
                                 />
                              </ListItemIcon>
                              <ListItemText primary='Bookings' />
                           </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                           <ListItemButton
                              component={RouterLink}
                              to={`/venues/${activeVenue.id}/settings`}
                              selected={location.pathname.startsWith(
                                 `/venues/${activeVenue.id}/settings`
                              )}
                              sx={{
                                 borderRadius: '0 20px 20px 0',
                                 pl: 2,
                                 pr: 1,
                                 py: 1,
                                 '&.Mui-selected': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                 },
                                 '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                 },
                              }}
                           >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                 <SettingsIcon
                                    color={
                                       location.pathname.startsWith(
                                          `/venues/${activeVenue.id}/settings`
                                       )
                                          ? 'primary'
                                          : 'inherit'
                                    }
                                 />
                              </ListItemIcon>
                              <ListItemText primary='Venue Settings' />
                           </ListItemButton>
                        </ListItem>

                        <Divider sx={{ my: 2 }} />
                     </>
                  )}
               </>
            ) : (
               <List sx={{ px: 1, mt: 2 }}>
                  <ListItem disablePadding>
                     <ListItemButton
                        component={RouterLink}
                        to='/login'
                        sx={{
                           borderRadius: 2,
                           mx: 1,
                           mb: 0.5,
                           color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                           '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.75)',
                           },
                        }}
                     >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                           <LoginIcon />
                        </ListItemIcon>
                        <ListItemText
                           primary='Login'
                           primaryTypographyProps={{
                              fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              color: 'inherit',
                           }}
                        />
                     </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                     <ListItemButton
                        component={RouterLink}
                        to='/signup'
                        sx={{
                           borderRadius: 2,
                           mx: 1,
                           mb: 0.5,
                           color: 'rgba(255, 255, 255, 0.60)', // textSecondary from theme
                           '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.75)',
                           },
                        }}
                     >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                           <PersonAddIcon />
                        </ListItemIcon>
                        <ListItemText
                           primary='Sign Up'
                           primaryTypographyProps={{
                              fontFamily: '"DM Sans", sans-serif', // DM Sans for nav items
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              color: 'inherit',
                           }}
                        />
                     </ListItemButton>
                  </ListItem>
               </List>
            )}
         </Drawer>

         {/* Main Content */}
         <Box
            component='main'
            sx={{
               flexGrow: 1,
               width: { xs: '100%', sm: `calc(100% - ${DRAWER_WIDTH}px)` },
               minWidth: 0,
               mt: 8, // Add margin top to account for app bar
               overflowX: 'hidden',
            }}
         >
            {/* {!isPersonalAccount && activeOrganization && (
               <Box
                  sx={{
                     bgcolor: 'secondary.50',
                     p: 1,
                     borderBottom: 1,
                     borderColor: 'divider',
                     display: 'flex',
                     alignItems: 'center',
                  }}
               >
                  <Avatar
                     src={activeOrganization.logoUrl || undefined}
                     sx={{ width: 24, height: 24, mr: 1 }}
                  >
                     {activeOrganization.name.charAt(0)}
                  </Avatar>
                  <Typography variant='body2' component='span'>
                     <span>You are viewing </span>
                     <strong>{activeOrganization.name}</strong>
                     <span>
                        {' '}
                        as{' '}
                        {activeOrganization.members.find((m) => m.userId === currentUser?.uid)
                           ?.role || 'member'}
                     </span>
                  </Typography>
                  <Button
                     size='small'
                     variant='text'
                     sx={{ ml: 'auto' }}
                     onClick={() => switchToOrganization(null)}
                  >
                     Switch to Personal
                  </Button>
               </Box>
            )} */}
            {children}
         </Box>

         {/* Location Request Modal - COMMENTED OUT */}
         {/* {isAuthenticated && currentUser && (
            <LocationRequestModal
               open={showLocationModal}
               onClose={closeLocationModal}
               onLocationSaved={onLocationSaved}
               onSkip={skipLocationModal}
               userId={currentUser.uid}
               userName={currentUser.displayName || currentUser.email?.split('@')[0]}
            />
         )} */}
      </Box>
   );
};

export default Layout;
